import { Col, Container, Row } from 'react-bootstrap';
import { Dispatch, SetStateAction } from 'react';
import IssueHistoryFilters, { IssueHistoryFiltersState } from '@/features/IssueHistory/components/IssueHistoryFilters';
import { TODAY, TODAY_EOD } from '@/constants';

import CausedByPopover from '@/features/IssueHistory/components/CausedByPopover';
import { DispatchIssueWithDocuments } from './components/IssueHistoryForm/types';
import Feature from '@/common/Feature';
import IssueHistoryTable from '@/features/IssueHistory/components/IssueHistoryTable';
import { ServiceIssueFiltersType } from '@/hooks/useServiceIssueFilters';
import { SortDirectionEnum } from '@/models/gen/graphql';
import { refetchDispatchIssueWithDocuments } from './api/services/searchDispatchIssue';
import { useProvider } from '@/common/Provider';

const IssueHistory = (): JSX.Element => {
  return (
    <Feature name="IssueHistory" context={initIssueHistoryState} methods={issueHistoryMethodConstructor}>
      <IssueHistoryFilters />
      <Container>
        <Row>
          <Col>
            <IssueHistoryTable />
          </Col>
        </Row>
        <CausedByPopover />
      </Container>
    </Feature>
  );
};

export type IssueHistoryRow = DispatchIssueWithDocuments;
export type IssueHistoryFilters = {
  startDatetime: string;
  endDatetime: string;
  stations: string[];
  tripServicerIataAirlineCodes: string[];
  taxiUsed: string | null;
  search: string;
};
export type IssueHistoryContext = {
  filters: ServiceIssueFiltersType;
  issues: Map<string, IssueHistoryRow>;
  totalIssues: number;
  selected: Map<string, IssueHistoryRow>;
  loading: boolean;
  sortBy: string;
  search: string;
  sortDirection: SortDirectionEnum;
};
export type IssueHistoryMethods = {
  resetState: (overrides?: Partial<IssueHistoryContext>) => void;
  submitFilters: (values?: Partial<IssueHistoryFiltersState>) => Promise<void>;
};
export const initIssueHistoryState: IssueHistoryContext = {
  filters: {
    startDatetime: TODAY,
    endDatetime: TODAY_EOD,
    airportCode: [],
    tripServicerIataAirlineCodes: [],
    taxiUsed: null,
  },
  issues: new Map(),
  totalIssues: 0,
  selected: new Map(),
  search: '',
  sortBy: '',
  sortDirection: SortDirectionEnum.Asc,
  loading: false,
};
export const issueHistoryMethodConstructor = (
  state: IssueHistoryContext,
  setState: Dispatch<SetStateAction<IssueHistoryContext>>
): IssueHistoryMethods => ({
  resetState: (overrides?: Partial<IssueHistoryContext>): void => setState({ ...initIssueHistoryState, ...(overrides || {}) }),
  submitFilters: async (values: ServiceIssueFiltersType = state.filters): Promise<void> => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));
      const res = await refetchDispatchIssueWithDocuments([values]);
      setState((current) => {
        const issues = new Map<string, IssueHistoryRow>(res.rows.map((issue) => [issue.id, issue]));
        const totalIssues = res.totalCount;
        return { ...current, issues, totalIssues };
      });
    } catch (err) {
      console.error('Error submitting filters:', err);
    } finally {
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  },
});
export function useIssueHistory<T = IssueHistoryContext>(
  selector?: (state: IssueHistoryContext) => T
): [T, Dispatch<SetStateAction<IssueHistoryContext>>, IssueHistoryMethods] {
  return useProvider<IssueHistoryContext, T, IssueHistoryMethods>(selector);
}

export default IssueHistory;
